/**
 * Version control over the meaning of an exploration.
 *
 * This module deliberately has no store, persistence, DOM or model dependency. It
 * turns a live Session into a canonical semantic document, then supplies the
 * content-addressed operations that document needs: commits, diffs, merges,
 * branch lineage and signed critiques.
 *
 * A semantic snapshot is narrower than a stored Session. Layout, animation,
 * folding, hand placement, document identity, spend, run accounts and the
 * operational receipt window do not change what the graph says. Node authorship,
 * source checks, the seed's survey, panels, audits and recorded outcomes do, so
 * those records travel whole. Keeping that boundary here prevents a camera move from becoming a commit
 * while preserving the provenance a reviewer needs to weigh one.
 */

import type {
  Audit,
  Bridge,
  Directive,
  EvidenceRebase,
  FieldSurvey,
  ForeignRef,
  Frame,
  ModelStamp,
  NodeElaboration,
  NodeKind,
  NodeOrigin,
  Outcome,
  PanelCheck,
  Session,
  Verification,
  VerificationArchiveEntry,
} from './types'
import type { ResearchState } from './research'

export const SEMANTIC_SNAPSHOT_VERSION = 1 as const
export const SEMANTIC_COMMIT_VERSION = 1 as const
export const BRANCH_LINEAGE_VERSION = 1 as const
export const SEMANTIC_WORKSPACE_VERSION = 1 as const
export const CRITIQUE_VERSION = 1 as const
export const CRITIQUE_ALGORITHM = 'ECDSA-P256-SHA256' as const

export type SemanticActorKind = 'person' | 'model' | 'source-check' | 'outcome' | 'system'

export interface PersonActor {
  kind: 'person'
  /** Stable within the collaboration space. A display name alone is not identity. */
  id: string
  name?: string
}

export interface ModelActor {
  kind: 'model'
  model: ModelStamp
  opId?: string
}

export interface SourceCheckActor {
  kind: 'source-check'
  nodeId: string
  model: ModelStamp
  verificationOpId?: string
}

export interface OutcomeActor {
  kind: 'outcome'
  nodeId: string
  recordedAt: number
  source?: string
}

export interface SystemActor {
  kind: 'system'
  /** Names the deterministic mechanism, not a human pretending to be one. */
  id: string
  name?: string
}

export type SemanticActor = PersonActor | ModelActor | SourceCheckActor | OutcomeActor | SystemActor

export interface SemanticProfile {
  seed: string
  /** Absence in Session means claim, so the canonical form never has two spellings. */
  frame: Frame
  brief: Directive[]
  /** Filing changes the research profile even though it changes no node. */
  project: string | null
}

export interface SemanticClaim {
  id: string
  parentId: string | null
  bridgeFrom?: string[]
  /**
   * The typed edges a PERSON drew — what this claim rests on, and what it argues
   * against. They belong in the claim block beside `parentId` and `bridgeFrom`
   * because they are the same kind of fact: how this claim is attached to the
   * rest of the argument. Left out, drawing an edge changed no hash, appeared in
   * no commit, crossed in no merge request, and could not be restored by a
   * checkout — a user-authored edit silently absent from the record built to
   * carry user-authored edits.
   */
  dependsOn?: string[]
  attacks?: string[]
  label: string
  claim: string
  reasoning: string
  compact?: true
  elaboration?: NodeElaboration
  kind: NodeKind
  createdAt: number
  createdOrdinal?: number
  editedAt?: number
  foreignFrom?: ForeignRef
  model?: ModelStamp
  origin: NodeOrigin
}

export interface SemanticEvidence {
  /** Model-authored pointers are evidence claims too, even before a check. */
  evidence: string[]
  verification?: Verification
  verificationHistory?: Verification[]
  evidenceRebases?: EvidenceRebase[]
  panel?: PanelCheck
  audit?: Audit
  outcome?: Outcome
  /** The seed's survey and the ones before it. A search that happened is
   * meaning in the evidence sense, not layout. Optional, and present only
   * where one ran, so every snapshot hashed before surveys existed — and every
   * seed never surveyed — keeps the hash it had. */
  survey?: FieldSurvey
  surveyHistory?: FieldSurvey[]
}

export interface SemanticConclusion {
  /**
   * The date this claim promised an answer by, when somebody set one.
   *
   * In the CONCLUSION block rather than the evidence one: nothing has been
   * observed yet, and what the field says is what the person expects this claim
   * to come to. It travels for the reason the typed edges do — it is theirs, and
   * a copy that lost it would lose a promise rather than a cache.
   */
  resolvesBy?: number
  confidence: number
  novelty: number
  tension: number
  nextSteps: string[]
  terminal: boolean
  terminalReason?: string
  terminalKind?: 'constraint'
}

export interface SemanticNode {
  claim: SemanticClaim
  evidence: SemanticEvidence
  conclusion: SemanticConclusion
}

export interface SemanticSnapshot {
  version: typeof SEMANTIC_SNAPSHOT_VERSION
  profile: SemanticProfile
  /** Research policy and findings are meaning. Revision metadata is excluded to
   * avoid a commit containing itself through `semanticCommits`. */
  research: SemanticResearch | null
  /** Records rather than arrays, so independently-added ids merge without an order conflict. */
  nodes: Record<string, SemanticNode>
  /** Candidate syntheses are semantic artefacts even before one is realised. */
  bridges: Record<string, Bridge>
  /** Orphaned checks retain their full source and model provenance. */
  verificationArchive: Record<string, VerificationArchiveEntry>
}

export type SemanticResearch = Pick<
  ResearchState,
  | 'evidenceProfile'
  | 'evidenceRecords'
  | 'genealogyOverrides'
  | 'coverageTraces'
  | 'evidenceAnnotations'
  | 'claimAnnotations'
  | 'decisionCriterionSignals'
  | 'claimScopes'
  | 'contradictionAnalyses'
  | 'falsificationPortfolios'
  | 'calibrationTournaments'
  | 'auditReceipts'
> & {
  /** Optional keeps hashes of v1 commits written before manual joins unchanged. */
  manualEvidenceBindings?: ResearchState['manualEvidenceBindings']
}

export type SemanticDiffSection = 'profile' | 'claims' | 'evidence' | 'conclusions'
export type SemanticChangeKind = 'added' | 'removed' | 'changed'
export type SemanticEntity = 'profile' | 'node' | 'bridge' | 'verification-archive'

export interface SemanticChange {
  section: SemanticDiffSection
  kind: SemanticChangeKind
  entity: SemanticEntity
  entityId: string
  /** Dot path inside the section. `*` means the complete section was added or removed. */
  field: string
  before?: unknown
  after?: unknown
}

export interface SemanticDiff {
  profile: SemanticChange[]
  claims: SemanticChange[]
  evidence: SemanticChange[]
  conclusions: SemanticChange[]
  hasChanges: boolean
}

export type SemanticConflictKind = 'field-divergence' | 'delete-vs-edit'

export interface SemanticMergeConflict {
  kind: SemanticConflictKind
  /** JSON Pointer into the semantic snapshot. */
  path: string
  base?: unknown
  ours?: unknown
  theirs?: unknown
}

export interface SemanticMergeResult {
  snapshot: SemanticSnapshot
  conflicts: SemanticMergeConflict[]
}

export interface SemanticCommit {
  readonly version: typeof SEMANTIC_COMMIT_VERSION
  readonly hash: string
  readonly parentHash: string | null
  readonly branchId: string
  readonly sequence: number
  readonly createdAt: number
  readonly actor: SemanticActor
  readonly message: string
  readonly snapshotHash: string
  readonly snapshot: SemanticSnapshot
}

export interface AppendSemanticCommitInput {
  branchId: string
  actor: SemanticActor
  message: string
  createdAt?: number
  /** Allows the first commit on a fork to point at a commit held in its parent log. */
  parentHash?: string | null
}

export interface SemanticCommitAppend {
  commit: SemanticCommit
  history: readonly SemanticCommit[]
}

export interface CommitLogCheck {
  valid: boolean
  at?: number
  reason?: string
}

export interface BranchLineage {
  version: typeof BRANCH_LINEAGE_VERSION
  branchId: string
  parentBranchId: string | null
  forkedFromCommit: string | null
  depth: number
  createdAt: number
  createdBy: SemanticActor
}

/**
 * The revision whose semantic snapshot the live Session is editing.
 *
 * This is deliberately revision metadata rather than part of a semantic
 * snapshot. Including it there would make checking out a branch change what the
 * research says, and would make a commit contain the name of itself.
 */
export interface SemanticWorkspace {
  version: typeof SEMANTIC_WORKSPACE_VERSION
  checkedOutBranchId: string
  checkedOutCommitHash: string
}

export interface CritiqueFinding {
  path: string
  severity: 'note' | 'concern' | 'blocking'
  note: string
}

export interface CritiquePayload {
  version: typeof CRITIQUE_VERSION
  critiqueId: string
  branch: BranchLineage
  baseCommitHash: string
  headCommitHash: string
  reviewer: PersonActor
  body: string
  findings: CritiqueFinding[]
  createdAt: number
}

export interface CritiqueBindingCheck {
  valid: boolean
  reason?: string
}

export interface CritiqueKeyPairJwk {
  publicKey: JsonWebKey
  privateKey: JsonWebKey
}

export interface SignedCritiqueEnvelope {
  version: typeof CRITIQUE_VERSION
  algorithm: typeof CRITIQUE_ALGORITHM
  payload: CritiquePayload
  payloadHash: string
  publicKey: JsonWebKey
  /** Raw Web Crypto signature, base64url without padding. */
  signature: string
}

type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue }

/**
 * RFC-8785-shaped JSON for the subset Pathways stores: sorted object keys,
 * original array order, finite numbers and no undefined object members.
 *
 * It is intentionally small rather than advertised as a complete JCS
 * implementation. Session data contains no bigint, Date, Map or custom toJSON
 * objects, and accepting one here would make browser implementations part of the
 * hash contract.
 */
function canonicalValue(value: unknown, stack: Set<object>): JsonValue | undefined {
  if (value === undefined) return undefined
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'number') {
    if (!Number.isFinite(value))
      throw new TypeError('Canonical semantic JSON requires finite numbers.')
    return Object.is(value, -0) ? 0 : value
  }
  if (typeof value !== 'object') {
    throw new TypeError(`Canonical semantic JSON cannot encode ${typeof value}.`)
  }
  if (stack.has(value)) throw new TypeError('Canonical semantic JSON cannot encode a cycle.')
  stack.add(value)
  try {
    if (Array.isArray(value)) {
      return value.map((item) => canonicalValue(item, stack) ?? null)
    }
    const out: Record<string, JsonValue> = {}
    for (const key of Object.keys(value).sort()) {
      const next = canonicalValue((value as Record<string, unknown>)[key], stack)
      if (next !== undefined) out[key] = next
    }
    return out
  } finally {
    stack.delete(value)
  }
}

export function canonicalSemanticJson(value: unknown): string {
  const canonical = canonicalValue(value, new Set())
  if (canonical === undefined) throw new TypeError('Canonical semantic JSON needs a value.')
  return JSON.stringify(canonical)
}

function cloneCanonical<T>(value: T): T {
  if (value === undefined) return value
  return JSON.parse(canonicalSemanticJson(value)) as T
}

/** Locale-free UTF-16 order keeps hashes and merges identical across devices. */
function lexical(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

function sortedRecord<T>(rows: Iterable<readonly [string, T]>): Record<string, T> {
  const out: Record<string, T> = {}
  for (const [key, value] of [...rows].sort(([a], [b]) => lexical(a, b))) out[key] = value
  return out
}

function snapshotNode(session: Session, id: string): SemanticNode | null {
  const node = session.nodes[id]
  if (!node) return null
  const claim: SemanticClaim = {
    id: node.id,
    parentId: node.parentId,
    ...(node.bridgeFrom?.length ? { bridgeFrom: [...node.bridgeFrom].sort() } : {}),
    // Sorted, like `bridgeFrom` above and for the same reason: the order two
    // edges were drawn in is not a difference between two people's copies, and
    // hashing it would make one.
    ...(node.dependsOn?.length ? { dependsOn: [...node.dependsOn].sort() } : {}),
    ...(node.attacks?.length ? { attacks: [...node.attacks].sort() } : {}),
    label: node.label,
    claim: node.claim,
    reasoning: node.reasoning,
    ...(node.compact ? { compact: true as const } : {}),
    ...(node.elaboration ? { elaboration: cloneCanonical(node.elaboration) } : {}),
    kind: node.kind,
    createdAt: node.createdAt,
    ...(node.createdOrdinal !== undefined ? { createdOrdinal: node.createdOrdinal } : {}),
    ...(node.editedAt !== undefined ? { editedAt: node.editedAt } : {}),
    ...(node.foreignFrom ? { foreignFrom: cloneCanonical(node.foreignFrom) } : {}),
    ...(node.model ? { model: cloneCanonical(node.model) } : {}),
    origin: node.origin,
  }
  const evidence: SemanticEvidence = {
    evidence: [...node.evidence],
    ...(node.verification ? { verification: cloneCanonical(node.verification) } : {}),
    ...(node.verificationHistory?.length
      ? { verificationHistory: cloneCanonical(node.verificationHistory) }
      : {}),
    ...(node.evidenceRebases?.length
      ? { evidenceRebases: cloneCanonical(node.evidenceRebases) }
      : {}),
    ...(node.panel ? { panel: cloneCanonical(node.panel) } : {}),
    ...(node.audit ? { audit: cloneCanonical(node.audit) } : {}),
    ...(node.outcome ? { outcome: cloneCanonical(node.outcome) } : {}),
    ...(node.survey ? { survey: cloneCanonical(node.survey) } : {}),
    ...(node.surveyHistory?.length ? { surveyHistory: cloneCanonical(node.surveyHistory) } : {}),
  }
  const conclusion: SemanticConclusion = {
    ...(node.resolvesBy !== undefined ? { resolvesBy: node.resolvesBy } : {}),
    confidence: node.confidence,
    novelty: node.novelty,
    tension: node.tension,
    nextSteps: [...node.nextSteps],
    terminal: node.terminal,
    ...(node.terminalReason !== undefined ? { terminalReason: node.terminalReason } : {}),
    ...(node.terminalKind !== undefined ? { terminalKind: node.terminalKind } : {}),
  }
  return { claim, evidence, conclusion }
}

/** The complete archive row is its identity: legacy checks need not carry an op id. */
function archiveKey(entry: VerificationArchiveEntry): string {
  return canonicalSemanticJson(entry)
}

export function semanticSnapshot(session: Session): SemanticSnapshot {
  const nodeRows: Array<readonly [string, SemanticNode]> = []
  // Read the record rather than `order`: an imported document with a valid node
  // omitted from the replay list must not make that claim disappear from a hash.
  for (const id of Object.keys(session.nodes).sort()) {
    const node = snapshotNode(session, id)
    if (node) nodeRows.push([id, node])
  }

  const bridgeRows = Object.values(session.bridges)
    .map((bridge) => cloneCanonical(bridge))
    .sort((a, b) => lexical(a.id, b.id))
    .map((bridge) => [bridge.id, bridge] as const)

  const archiveRows = (session.verificationArchive ?? [])
    .map((entry) => cloneCanonical(entry))
    .sort((a, b) => lexical(archiveKey(a), archiveKey(b)))
    .map((entry) => [archiveKey(entry), entry] as const)

  return cloneCanonical({
    version: SEMANTIC_SNAPSHOT_VERSION,
    profile: {
      seed: session.seed,
      frame: session.frame ?? 'claim',
      brief: cloneCanonical(session.brief ?? []),
      project: session.project ?? null,
    },
    research: session.research
      ? {
          evidenceProfile: session.research.evidenceProfile,
          evidenceRecords: session.research.evidenceRecords,
          ...(session.research.manualEvidenceBindings.length
            ? { manualEvidenceBindings: session.research.manualEvidenceBindings }
            : {}),
          genealogyOverrides: session.research.genealogyOverrides,
          coverageTraces: session.research.coverageTraces,
          evidenceAnnotations: session.research.evidenceAnnotations,
          claimAnnotations: session.research.claimAnnotations,
          decisionCriterionSignals: session.research.decisionCriterionSignals,
          claimScopes: session.research.claimScopes,
          contradictionAnalyses: session.research.contradictionAnalyses,
          falsificationPortfolios: session.research.falsificationPortfolios,
          calibrationTournaments: session.research.calibrationTournaments,
          auditReceipts: session.research.auditReceipts,
        }
      : null,
    nodes: sortedRecord(nodeRows),
    bridges: sortedRecord(bridgeRows),
    verificationArchive: sortedRecord(archiveRows),
  })
}

function isSemanticSnapshot(value: Session | SemanticSnapshot): value is SemanticSnapshot {
  return 'version' in value && value.version === SEMANTIC_SNAPSHOT_VERSION && 'profile' in value
}

function asSnapshot(value: Session | SemanticSnapshot): SemanticSnapshot {
  return isSemanticSnapshot(value) ? cloneCanonical(value) : semanticSnapshot(value)
}

function cryptoApi(): Crypto {
  const api = globalThis.crypto
  if (!api?.subtle) throw new Error('Web Crypto is unavailable in this environment.')
  return api
}

function bytesOf(value: string | Uint8Array): Uint8Array<ArrayBuffer> {
  return typeof value === 'string' ? new TextEncoder().encode(value) : new Uint8Array(value)
}

export async function sha256Hex(value: string | Uint8Array): Promise<string> {
  const digest = await cryptoApi().subtle.digest('SHA-256', bytesOf(value))
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

export async function semanticSnapshotHash(value: Session | SemanticSnapshot): Promise<string> {
  return sha256Hex(canonicalSemanticJson(asSnapshot(value)))
}

const equal = (a: unknown, b: unknown): boolean =>
  canonicalSemanticJson(a) === canonicalSemanticJson(b)

function objectRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function changeKind(before: unknown, after: unknown): SemanticChangeKind {
  if (before === undefined) return 'added'
  if (after === undefined) return 'removed'
  return 'changed'
}

function collectChanges(
  out: SemanticChange[],
  section: SemanticDiffSection,
  entity: SemanticEntity,
  entityId: string,
  before: unknown,
  after: unknown,
  path: string[] = [],
): void {
  if (before === undefined && after === undefined) return
  if (before !== undefined && after !== undefined && equal(before, after)) return
  const a = objectRecord(before)
  const b = objectRecord(after)
  if (a && b) {
    const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort()
    for (const key of keys) {
      collectChanges(out, section, entity, entityId, a[key], b[key], [...path, key])
    }
    return
  }
  out.push({
    section,
    kind: changeKind(before, after),
    entity,
    entityId,
    field: path.length ? path.join('.') : '*',
    ...(before !== undefined ? { before: cloneCanonical(before) } : {}),
    ...(after !== undefined ? { after: cloneCanonical(after) } : {}),
  })
}

const changeOrder = (a: SemanticChange, b: SemanticChange): number =>
  lexical(a.entity, b.entity) ||
  lexical(a.entityId, b.entityId) ||
  lexical(a.field, b.field) ||
  lexical(a.kind, b.kind)

export function diffSemanticSnapshots(
  beforeValue: Session | SemanticSnapshot,
  afterValue: Session | SemanticSnapshot,
): SemanticDiff {
  const before = asSnapshot(beforeValue)
  const after = asSnapshot(afterValue)
  const diff: SemanticDiff = {
    profile: [],
    claims: [],
    evidence: [],
    conclusions: [],
    hasChanges: false,
  }

  collectChanges(diff.profile, 'profile', 'profile', 'session', before.profile, after.profile)
  collectChanges(
    diff.profile,
    'profile',
    'profile',
    'evidence-profile',
    before.research?.evidenceProfile,
    after.research?.evidenceProfile,
  )
  collectChanges(
    diff.evidence,
    'evidence',
    'profile',
    'research-evidence',
    before.research
      ? {
          evidenceRecords: before.research.evidenceRecords,
          manualEvidenceBindings: before.research.manualEvidenceBindings ?? [],
          genealogyOverrides: before.research.genealogyOverrides,
          coverageTraces: before.research.coverageTraces,
          evidenceAnnotations: before.research.evidenceAnnotations,
          claimAnnotations: before.research.claimAnnotations,
          decisionCriterionSignals: before.research.decisionCriterionSignals,
          auditReceipts: before.research.auditReceipts,
        }
      : undefined,
    after.research
      ? {
          evidenceRecords: after.research.evidenceRecords,
          manualEvidenceBindings: after.research.manualEvidenceBindings ?? [],
          genealogyOverrides: after.research.genealogyOverrides,
          coverageTraces: after.research.coverageTraces,
          evidenceAnnotations: after.research.evidenceAnnotations,
          claimAnnotations: after.research.claimAnnotations,
          decisionCriterionSignals: after.research.decisionCriterionSignals,
          auditReceipts: after.research.auditReceipts,
        }
      : undefined,
  )
  collectChanges(
    diff.claims,
    'claims',
    'profile',
    'research-scopes',
    before.research?.claimScopes,
    after.research?.claimScopes,
  )
  collectChanges(
    diff.conclusions,
    'conclusions',
    'profile',
    'research-findings',
    before.research
      ? {
          contradictionAnalyses: before.research.contradictionAnalyses,
          falsificationPortfolios: before.research.falsificationPortfolios,
          calibrationTournaments: before.research.calibrationTournaments,
        }
      : undefined,
    after.research
      ? {
          contradictionAnalyses: after.research.contradictionAnalyses,
          falsificationPortfolios: after.research.falsificationPortfolios,
          calibrationTournaments: after.research.calibrationTournaments,
        }
      : undefined,
  )

  const nodeIds = [...new Set([...Object.keys(before.nodes), ...Object.keys(after.nodes)])].sort()
  for (const id of nodeIds) {
    const a = before.nodes[id]
    const b = after.nodes[id]
    collectChanges(diff.claims, 'claims', 'node', id, a?.claim, b?.claim)
    collectChanges(diff.evidence, 'evidence', 'node', id, a?.evidence, b?.evidence)
    collectChanges(diff.conclusions, 'conclusions', 'node', id, a?.conclusion, b?.conclusion)
  }

  const bridgeIds = [
    ...new Set([...Object.keys(before.bridges), ...Object.keys(after.bridges)]),
  ].sort()
  for (const id of bridgeIds) {
    collectChanges(
      diff.conclusions,
      'conclusions',
      'bridge',
      id,
      before.bridges[id],
      after.bridges[id],
    )
  }

  const archiveIds = [
    ...new Set([
      ...Object.keys(before.verificationArchive),
      ...Object.keys(after.verificationArchive),
    ]),
  ].sort()
  for (const id of archiveIds) {
    collectChanges(
      diff.evidence,
      'evidence',
      'verification-archive',
      id,
      before.verificationArchive[id],
      after.verificationArchive[id],
    )
  }

  diff.profile.sort(changeOrder)
  diff.claims.sort(changeOrder)
  diff.evidence.sort(changeOrder)
  diff.conclusions.sort(changeOrder)
  diff.hasChanges =
    diff.profile.length + diff.claims.length + diff.evidence.length + diff.conclusions.length > 0
  return diff
}

const ABSENT = Symbol('semantic-absent')
type MergeValue = unknown | typeof ABSENT

function mergeEqual(a: MergeValue, b: MergeValue): boolean {
  if (a === ABSENT || b === ABSENT) return a === b
  return equal(a, b)
}

function conflictValue(value: MergeValue): unknown {
  return value === ABSENT ? undefined : cloneCanonical(value)
}

function pointer(parts: readonly string[]): string {
  return `/${parts.map((part) => part.replace(/~/g, '~0').replace(/\//g, '~1')).join('/')}`
}

function deterministicAddedChoice(a: unknown, b: unknown): unknown {
  return lexical(canonicalSemanticJson(a), canonicalSemanticJson(b)) <= 0
    ? cloneCanonical(a)
    : cloneCanonical(b)
}

function isPrefix(base: unknown[], side: unknown[]): boolean {
  return base.length <= side.length && base.every((value, index) => equal(value, side[index]))
}

function mergeAppendedArrays(
  base: unknown[],
  ours: unknown[],
  theirs: unknown[],
): unknown[] | null {
  if (!isPrefix(base, ours) || !isPrefix(base, theirs)) return null
  const additions = [...ours.slice(base.length), ...theirs.slice(base.length)]
  const unique = new Map<string, unknown>()
  for (const value of additions) unique.set(canonicalSemanticJson(value), value)
  return [
    ...cloneCanonical(base),
    ...[...unique.entries()]
      .sort(([a], [b]) => lexical(a, b))
      .map(([, value]) => cloneCanonical(value)),
  ]
}

function mergeValue(
  base: MergeValue,
  ours: MergeValue,
  theirs: MergeValue,
  path: string[],
  conflicts: SemanticMergeConflict[],
): MergeValue {
  if (mergeEqual(ours, theirs)) return ours === ABSENT ? ABSENT : cloneCanonical(ours)
  if (mergeEqual(ours, base)) return theirs === ABSENT ? ABSENT : cloneCanonical(theirs)
  if (mergeEqual(theirs, base)) return ours === ABSENT ? ABSENT : cloneCanonical(ours)

  if (base !== ABSENT && (ours === ABSENT || theirs === ABSENT)) {
    const edited = ours === ABSENT ? theirs : ours
    conflicts.push({
      kind: 'delete-vs-edit',
      path: pointer(path),
      base: conflictValue(base),
      ours: conflictValue(ours),
      theirs: conflictValue(theirs),
    })
    // Retaining the edited value avoids losing either the base record or the work
    // done on it while the conflict waits for an explicit resolution.
    return edited === ABSENT ? cloneCanonical(base) : cloneCanonical(edited)
  }

  const baseObject = base === ABSENT ? {} : objectRecord(base)
  const oursObject = ours === ABSENT ? null : objectRecord(ours)
  const theirsObject = theirs === ABSENT ? null : objectRecord(theirs)
  if (baseObject && oursObject && theirsObject) {
    const out: Record<string, unknown> = {}
    const keys = [
      ...new Set([
        ...Object.keys(baseObject),
        ...Object.keys(oursObject),
        ...Object.keys(theirsObject),
      ]),
    ].sort()
    for (const key of keys) {
      const merged = mergeValue(
        Object.prototype.hasOwnProperty.call(baseObject, key) ? baseObject[key] : ABSENT,
        Object.prototype.hasOwnProperty.call(oursObject, key) ? oursObject[key] : ABSENT,
        Object.prototype.hasOwnProperty.call(theirsObject, key) ? theirsObject[key] : ABSENT,
        [...path, key],
        conflicts,
      )
      if (merged !== ABSENT) out[key] = merged
    }
    return out
  }

  if (Array.isArray(ours) && Array.isArray(theirs)) {
    const baseArray = base === ABSENT ? [] : Array.isArray(base) ? base : null
    if (baseArray) {
      const appended = mergeAppendedArrays(baseArray, ours, theirs)
      if (appended) return appended
    }
  }

  conflicts.push({
    kind: 'field-divergence',
    path: pointer(path),
    base: conflictValue(base),
    ours: conflictValue(ours),
    theirs: conflictValue(theirs),
  })
  // Existing fields stay at the common base until resolved. Two concurrent adds
  // have no base, so choose canonically rather than making "ours" an invisible
  // priority. The conflict still carries both candidates.
  if (base !== ABSENT) return cloneCanonical(base)
  if (ours === ABSENT) return theirs === ABSENT ? ABSENT : cloneCanonical(theirs)
  if (theirs === ABSENT) return cloneCanonical(ours)
  return deterministicAddedChoice(ours, theirs)
}

export function mergeSemanticSnapshots(
  baseValue: Session | SemanticSnapshot,
  oursValue: Session | SemanticSnapshot,
  theirsValue: Session | SemanticSnapshot,
): SemanticMergeResult {
  const base = asSnapshot(baseValue)
  const ours = asSnapshot(oursValue)
  const theirs = asSnapshot(theirsValue)
  const conflicts: SemanticMergeConflict[] = []
  const merged = mergeValue(base, ours, theirs, [], conflicts)
  if (merged === ABSENT) throw new Error('A semantic merge cannot delete its snapshot root.')
  conflicts.sort((a, b) => lexical(a.path, b.path) || lexical(a.kind, b.kind))
  return { snapshot: cloneCanonical(merged as SemanticSnapshot), conflicts }
}

function commitBody(commit: Omit<SemanticCommit, 'hash' | 'snapshot'>): unknown {
  return {
    version: commit.version,
    parentHash: commit.parentHash,
    branchId: commit.branchId,
    sequence: commit.sequence,
    createdAt: commit.createdAt,
    actor: commit.actor,
    message: commit.message,
    snapshotHash: commit.snapshotHash,
  }
}

async function hashCommitBody(commit: Omit<SemanticCommit, 'hash' | 'snapshot'>): Promise<string> {
  return sha256Hex(canonicalSemanticJson(commitBody(commit)))
}

function freezeDeep<T>(value: T): T {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  for (const child of Object.values(value as Record<string, unknown>)) freezeDeep(child)
  return Object.freeze(value)
}

export async function verifySemanticCommitLog(
  history: readonly SemanticCommit[],
): Promise<CommitLogCheck> {
  for (let i = 0; i < history.length; i++) {
    const commit = history[i]!
    if (commit.version !== SEMANTIC_COMMIT_VERSION) {
      return { valid: false, at: i, reason: 'Unsupported semantic commit version.' }
    }
    if (commit.sequence !== i) {
      return { valid: false, at: i, reason: 'Commit sequence is not append-only.' }
    }
    if (i > 0) {
      const previous = history[i - 1]!
      if (commit.branchId !== previous.branchId) {
        return { valid: false, at: i, reason: 'One commit log cannot change branch ids.' }
      }
      if (commit.parentHash !== previous.hash) {
        return { valid: false, at: i, reason: 'Commit parent does not name the preceding commit.' }
      }
    }
    const snapshotHash = await semanticSnapshotHash(commit.snapshot)
    if (snapshotHash !== commit.snapshotHash) {
      return { valid: false, at: i, reason: 'Commit snapshot hash does not match its snapshot.' }
    }
    const hash = await hashCommitBody(commit)
    if (hash !== commit.hash) {
      return { valid: false, at: i, reason: 'Commit hash does not match its metadata.' }
    }
  }
  return { valid: true }
}

export async function appendSemanticCommit(
  history: readonly SemanticCommit[],
  value: Session | SemanticSnapshot,
  input: AppendSemanticCommitInput,
): Promise<SemanticCommitAppend> {
  const checked = await verifySemanticCommitLog(history)
  if (!checked.valid) {
    throw new Error(
      `Cannot append to an invalid semantic history: ${checked.reason ?? 'unknown error'}`,
    )
  }
  const previous = history[history.length - 1]
  if (previous && previous.branchId !== input.branchId) {
    throw new Error('Cannot append a different branch id to one semantic commit log.')
  }
  if (previous && input.parentHash !== undefined && input.parentHash !== previous.hash) {
    throw new Error('The supplied parent hash is not the current semantic head.')
  }
  if (!isSemanticSnapshot(value) && value.research) {
    const workspace = value.research.semanticWorkspace
    const expectedHead = previous?.hash ?? input.parentHash ?? null
    if (!workspace && (expectedHead || value.research.semanticCommits.length > 0)) {
      throw new Error('Check out this semantic branch before committing to it.')
    }
    if (workspace && workspace.checkedOutBranchId !== input.branchId) {
      throw new Error('The live semantic state belongs to a different checked-out branch.')
    }
    if (workspace && workspace.checkedOutCommitHash !== expectedHead) {
      throw new Error('The checked-out revision is not the selected branch head.')
    }
  }
  const snapshot = asSnapshot(value)
  const snapshotHash = await semanticSnapshotHash(snapshot)
  const body = {
    version: SEMANTIC_COMMIT_VERSION,
    parentHash: previous?.hash ?? input.parentHash ?? null,
    branchId: input.branchId,
    sequence: previous ? previous.sequence + 1 : 0,
    createdAt: input.createdAt ?? Date.now(),
    actor: cloneCanonical(input.actor),
    message: input.message.trim(),
    snapshotHash,
  }
  const commit: SemanticCommit = freezeDeep({
    ...body,
    hash: await hashCommitBody(body),
    snapshot,
  })
  return { commit, history: Object.freeze([...history, commit]) }
}

export function createRootBranchLineage(
  branchId: string,
  createdBy: SemanticActor,
  createdAt = Date.now(),
): BranchLineage {
  return cloneCanonical({
    version: BRANCH_LINEAGE_VERSION,
    branchId,
    parentBranchId: null,
    forkedFromCommit: null,
    depth: 0,
    createdAt,
    createdBy,
  })
}

export function forkBranchLineage(
  parent: BranchLineage,
  branchId: string,
  forkedFromCommit: string,
  createdBy: SemanticActor,
  createdAt = Date.now(),
): BranchLineage {
  if (branchId === parent.branchId) throw new Error('A fork needs a new branch id.')
  if (!forkedFromCommit) throw new Error('A fork must name the commit it starts from.')
  return cloneCanonical({
    version: BRANCH_LINEAGE_VERSION,
    branchId,
    parentBranchId: parent.branchId,
    forkedFromCommit,
    depth: parent.depth + 1,
    createdAt,
    createdBy,
  })
}

export function semanticWorkspaceAt(branchId: string, commitHash: string): SemanticWorkspace {
  if (!branchId.trim()) throw new Error('A semantic workspace needs a branch id.')
  if (!commitHash.trim()) throw new Error('A semantic workspace needs a committed revision.')
  return {
    version: SEMANTIC_WORKSPACE_VERSION,
    checkedOutBranchId: branchId,
    checkedOutCommitHash: commitHash,
  }
}

/**
 * Resolve the state a branch means. A new fork inherits its captured parent
 * revision until it records a commit of its own; it never inherits whatever
 * happens to be visible in the live Session later.
 */
export function semanticBranchHead(
  commits: readonly SemanticCommit[],
  lineages: readonly BranchLineage[],
  branchId: string,
): SemanticCommit | null {
  const lineage = lineages.find((row) => row.branchId === branchId)
  if (!lineage) throw new Error(`Unknown semantic branch: ${branchId}`)
  const history = commits
    .filter((row) => row.branchId === branchId)
    .sort((a, b) => a.sequence - b.sequence || lexical(a.hash, b.hash))

  if (history.length) {
    for (const [index, commit] of history.entries()) {
      if (commit.sequence !== index) {
        throw new Error(`Semantic branch ${branchId} has a non-contiguous commit sequence.`)
      }
      const expectedParent = index > 0 ? history[index - 1]!.hash : lineage.forkedFromCommit
      if (commit.parentHash !== expectedParent) {
        throw new Error(`Semantic branch ${branchId} is not bound to its recorded lineage.`)
      }
    }
    return history.at(-1)!
  }

  if (lineage.forkedFromCommit === null) return null
  const inherited = commits.find((row) => row.hash === lineage.forkedFromCommit)
  if (!inherited || inherited.branchId !== lineage.parentBranchId) {
    throw new Error(`Semantic branch ${branchId} has a missing fork revision.`)
  }
  return inherited
}

/** Root-to-leaf lineage, failing closed on a missing parent or a cycle. */
export function traceBranchLineage(
  rows: readonly BranchLineage[],
  branchId: string,
): BranchLineage[] {
  const byId = new Map(rows.map((row) => [row.branchId, row]))
  const reverse: BranchLineage[] = []
  const seen = new Set<string>()
  let current = byId.get(branchId)
  if (!current) throw new Error(`Unknown semantic branch: ${branchId}`)
  while (current) {
    if (seen.has(current.branchId)) throw new Error('Semantic branch lineage contains a cycle.')
    seen.add(current.branchId)
    reverse.push(cloneCanonical(current))
    if (current.parentBranchId === null) break
    const parent = byId.get(current.parentBranchId)
    if (!parent) throw new Error(`Missing parent semantic branch: ${current.parentBranchId}`)
    current = parent
  }
  return reverse.reverse()
}

export function branchDescendsFrom(
  rows: readonly BranchLineage[],
  branchId: string,
  possibleAncestorId: string,
): boolean {
  return traceBranchLineage(rows, branchId).some((row) => row.branchId === possibleAncestorId)
}

/**
 * A signature proves that one key signed some bytes. This second check proves
 * those bytes review an actual revision on the branch path present here.
 */
export function critiqueBindingCheck(
  payload: CritiquePayload,
  commits: readonly SemanticCommit[],
  lineages: readonly BranchLineage[],
): CritiqueBindingCheck {
  const recordedLineage = lineages.find((row) => row.branchId === payload.branch.branchId)
  if (!recordedLineage) return { valid: false, reason: 'Critique branch is not present.' }
  if (!equal(recordedLineage, payload.branch)) {
    return { valid: false, reason: 'Critique branch lineage does not match the recorded lineage.' }
  }

  let path: BranchLineage[]
  try {
    path = traceBranchLineage(lineages, payload.branch.branchId)
  } catch {
    return { valid: false, reason: 'Critique branch lineage is incomplete.' }
  }
  const depthByBranch = new Map(path.map((row, index) => [row.branchId, index]))
  const byHash = new Map(commits.map((row) => [row.hash, row]))
  const base = byHash.get(payload.baseCommitHash)
  const head = byHash.get(payload.headCommitHash)
  if (!base || !head) {
    return { valid: false, reason: 'Critique names a revision that is not present.' }
  }
  if (head.branchId !== payload.branch.branchId) {
    return { valid: false, reason: 'Critique head is not a revision of its named branch.' }
  }

  let cursor: SemanticCommit | undefined = head
  let priorDepth = depthByBranch.get(head.branchId)
  const seen = new Set<string>()
  while (cursor) {
    if (seen.has(cursor.hash)) {
      return { valid: false, reason: 'Critique revision ancestry contains a cycle.' }
    }
    seen.add(cursor.hash)
    const depth = depthByBranch.get(cursor.branchId)
    if (depth === undefined || (priorDepth !== undefined && depth > priorDepth)) {
      return { valid: false, reason: 'Critique revision leaves its recorded branch path.' }
    }
    if (cursor.hash === base.hash) return { valid: true }
    priorDepth = depth
    cursor = cursor.parentHash ? byHash.get(cursor.parentHash) : undefined
  }
  return { valid: false, reason: 'Critique base is not an ancestor of its head.' }
}

function base64Url(bytes: Uint8Array): string {
  const binary = String.fromCharCode(...bytes)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new TypeError('Invalid base64url signature.')
  const padded =
    value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  // Reject alternate spellings whose unused low bits decode to the same bytes.
  // A signature envelope is canonical data; changing its text must never verify
  // merely because a permissive base64 decoder discards the changed bits.
  if (base64Url(bytes) !== value) throw new TypeError('Non-canonical base64url signature.')
  if (bytes.length !== 64) throw new TypeError('Invalid P-256 signature length.')
  return bytes
}

export async function generateCritiqueKeyPair(): Promise<CritiqueKeyPairJwk> {
  const keys = (await cryptoApi().subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, [
    'sign',
    'verify',
  ])) as CryptoKeyPair
  return {
    publicKey: cloneCanonical(await cryptoApi().subtle.exportKey('jwk', keys.publicKey)),
    privateKey: cloneCanonical(await cryptoApi().subtle.exportKey('jwk', keys.privateKey)),
  }
}

export async function critiquePayloadHash(payload: CritiquePayload): Promise<string> {
  return sha256Hex(canonicalSemanticJson(payload))
}

export async function signCritique(
  payload: CritiquePayload,
  keys: CritiqueKeyPairJwk,
): Promise<SignedCritiqueEnvelope> {
  const canonicalPayload = canonicalSemanticJson(payload)
  const privateKey = await cryptoApi().subtle.importKey(
    'jwk',
    keys.privateKey,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign'],
  )
  const signature = await cryptoApi().subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    bytesOf(canonicalPayload),
  )
  return cloneCanonical({
    version: CRITIQUE_VERSION,
    algorithm: CRITIQUE_ALGORITHM,
    payload,
    payloadHash: await sha256Hex(canonicalPayload),
    publicKey: keys.publicKey,
    signature: base64Url(new Uint8Array(signature)),
  })
}

export async function verifyCritique(envelope: SignedCritiqueEnvelope): Promise<boolean> {
  try {
    if (
      envelope.version !== CRITIQUE_VERSION ||
      envelope.algorithm !== CRITIQUE_ALGORITHM ||
      (await critiquePayloadHash(envelope.payload)) !== envelope.payloadHash
    ) {
      return false
    }
    const publicKey = await cryptoApi().subtle.importKey(
      'jwk',
      envelope.publicKey,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    )
    return cryptoApi().subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      publicKey,
      fromBase64Url(envelope.signature),
      bytesOf(canonicalSemanticJson(envelope.payload)),
    )
  } catch {
    return false
  }
}
